import React, { useState, useEffect, useRef } from "react";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonFooter,
  IonToolbar,
  IonText,
  IonButton,
  IonButtons,
  IonCardSubtitle,
  IonCardTitle,
  IonCardHeader,
  IonCardContent,
  IonCard,
  IonSelect,
  IonSelectOption,
  IonGrid,
  IonRow,
  IonCol,
  IonLabel,
  IonCheckbox,
  IonItem,
} from "@ionic/react";

import {
  BleClient,
  numbersToDataView,
  numberToUUID,
} from "@capacitor-community/bluetooth-le";
import { Buffer } from "buffer";
import ProgressBar from "@ramonak/react-progress-bar";
import FlashMaster from "./flashMaster";
import ParcCibles from "./parcCibles";
import FlashUsine from "./flashUsine";

import "./ota.scss";
import { extractVersion, sortByVersionDesc } from "./versions";
var version = "V2.8";
//var CRC32 = require("crc-32");
var ACK = 1;
var clientDevice: any;
var cmdStatus = 0;
var fwStatus = 0;
var expected_index = 0;
// ✅ ACK piloté par la notification (au lieu d'un polling 50 ms) : résout
// l'attente dès l'arrivée de l'ACK. N'affecte NI le protocole NI la taille de
// paquet (510 o) -> reste compatible avec les cibles 4.10/5.06.
var fwAckResolver: ((v: boolean) => void) | null = null;
var cmdAckResolver: ((v: boolean) => void) | null = null;

// UUIDs du service OTA
const OTA_SERVICE = "00008018-0000-1000-8000-00805f9b34fb";
const FW_CHAR = "00008020-0000-1000-8000-00805f9b34fb";
const CMD_CHAR = "00008022-0000-1000-8000-00805f9b34fb";

// ✅ V1.7 — File d'attente du parc : les mises à jour s'enchaînent toutes seules.
// On peut ajouter des appareils (du même type uniquement) à tout moment,
// y compris pendant qu'un flash est en cours.
type QueueStatus = "pending" | "updating" | "done" | "failed" | "skipped";
type QueueItem = {
  deviceId: string;
  name: string;
  status: QueueStatus;
  detail: string; // durée si OK, message d'erreur si échec
  progress: number;
  curVersion: string; // version FW actuelle lue sur l'appareil ("" si inconnue)
};


const Ota: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [files, setFiles] = useState<string[]>([]);
  const [device, setDevice] = useState<string>("");
  const [Model, setModel] = useState<string>("");
  const [Manufacturer, setManufacturer] = useState<string>("");
  const [HWVersion, setHWVersion] = useState<string>("");
  const [isFileLoaded, setIsFileLoaded] = useState(false);
  const [SWVersion, setSWVersion] = useState<string>("");
  const [DISInfo, setDis] = useState(false);
  const [isConnected, setConnection] = useState(false);
  const [showBar, setProgressBar] = useState(false);
  const [otaFile, setOtaFile] = useState<ArrayBuffer>();
  const [progress, setProgress] = useState(0);
  const [upload_speed, setUploadSpeed] = useState(0.00);
  const [barStatus, setBarStatus] = useState<string>("danger");
  const [showStatus, setShowStatus] = useState(false);
  const [status, setStatus] = useState<string>("");
  // ✅ Chronomètre de durée de la mise à jour (affiché à l'écran)
  const [elapsed, setElapsed] = useState(0); // secondes écoulées
  const chronoTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const chronoStart = useRef<number>(0);

  // ✅ File d'attente du parc
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const queueRef = useRef<QueueItem[]>([]);
  const [running, setRunning] = useState(false);
  const runningRef = useRef(false);
  // ✅ Forcer la mise à jour même si la version est déjà à jour
  const [forceUpdate, setForceUpdate] = useState(false);
  // ✅ Mise à jour du Master par USB : flux série indépendant du flux BLE
  const [modeMaster, setModeMaster] = useState(false);
  // Espace « parc » : met à jour le Master, puis lui charge l'image de cible
  // qu'il redistribuera. Séparé du flux BLE, qui flashe un appareil à la fois.
  const [modeParc, setModeParc] = useState(false);
  // Mise en service d'une carte neuve par USB : réservé aux techniciens, c'est
  // le seul écran qui écrit le bootloader et la table de partitions.
  const [modeUsine, setModeUsine] = useState(false);

  // Pilotes USB-série : l'écran a rejoint l'environnement technicien (V2.8), où
  // il est à sa place — on n'y va que quand une carte refuse d'apparaître dans
  // la fenêtre de choix du port, ce qui est déjà du dépannage.

  // ✅ V1.9 — écran maintenu allumé, comme dans l'appli de course (Screen Wake
  // Lock API). Un flash dure plusieurs minutes pendant lesquelles personne ne
  // touche l'écran : la mise en veille du poste coupait le transfert en cours.
  const wakeLock = useRef<any>(null);
  useEffect(() => {
    const nav: any = navigator;
    if (!nav.wakeLock) return; // API absente (iOS < 16.4, vieille WebView)

    const acquire = async () => {
      if (wakeLock.current || document.visibilityState !== "visible") return;
      try {
        wakeLock.current = await nav.wakeLock.request("screen");
        wakeLock.current.addEventListener("release", () => {
          wakeLock.current = null;
        });
      } catch (e) {
        console.log("Wake Lock refusé", e);
      }
    };

    // Le navigateur relâche le verrou dès que la page passe en arrière-plan et
    // ne le rend jamais de lui-même : il faut le redemander au retour, sinon
    // un simple changement d'onglet suffit à perdre la protection.
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") acquire();
    };

    acquire();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      wakeLock.current?.release().catch(() => {});
      wakeLock.current = null;
    };
  }, []);

  // Version cible = numéro extrait du nom du fichier .bin sélectionné
  const targetVersion = extractVersion(selectedFile);

  // Décide si un appareil doit être sauté : version identique et pas de forçage
  const skipDecision = (curVer: string, force: boolean, target: string | null) => {
    if (force) return false;
    const cv = extractVersion(curVer);
    return !!cv && !!target && cv === target;
  };

  // Lit la version FW (DIS 0x2a26) d'un appareil déjà connecté
  const readSwVersion = async (deviceId: string): Promise<string> => {
    try {
      const r = await BleClient.read(deviceId, numberToUUID(0x180a), numberToUUID(0x2a26), {
        timeout: 500,
      });
      return ab2str(r.buffer);
    } catch (e) {
      return "";
    }
  };

  const syncQueue = () => setQueue([...queueRef.current]);
  const patchItem = (deviceId: string, patch: Partial<QueueItem>) => {
    const it = queueRef.current.find((q) => q.deviceId === deviceId);
    if (it) Object.assign(it, patch);
    syncQueue();
  };

  // Cibles avec cibles, carabines avec carabines : type déduit du nom BLE
  const deviceTypeOf = (name?: string | null) => {
    if (!name) return null;
    if (name.includes("BZBOX FLASH")) return "BZBOX";
    if (name.includes("KARABINE FLASH")) return "KARABINE";
    return null;
  };

  const startChrono = () => {
    chronoStart.current = new Date().getTime();
    setElapsed(0);
    if (chronoTimer.current) clearInterval(chronoTimer.current);
    chronoTimer.current = setInterval(() => {
      setElapsed((new Date().getTime() - chronoStart.current) / 1000);
    }, 250);
  };

  const stopChrono = () => {
    if (chronoTimer.current) {
      clearInterval(chronoTimer.current);
      chronoTimer.current = null;
    }
    if (chronoStart.current) {
      // Fige la durée finale exacte
      setElapsed((new Date().getTime() - chronoStart.current) / 1000);
    }
  };

  const formatDuration = (s: number) => {
    const total = Math.floor(s);
    const m = Math.floor(total / 60);
    const sec = total % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const onDisconnect = (deviceId: string) => {
    console.log(`device ${deviceId} disconnected`);
    stopChrono();
    setConnection(false);
    setIsFileLoaded(false);
    setProgressBar(false);
  };

  function crc16(init: number, data: Uint8Array | Buffer, len: number): number {
    let crc = init;

    for (let i = 0; i < len; i++) {
      crc ^= data[i] << 8;

      for (let j = 0; j < 8; j++) {
        if (crc & 0x8000) {
          crc = ((crc << 1) ^ 0x1021) & 0xffff;
        } else {
          crc <<= 1;
        }
      }
    }

    return crc & 0xffff;
  }
  const ab2str = (buf: ArrayBuffer | ArrayBufferLike) => {
    return new TextDecoder().decode(buf);
  };

  async function send_firmware(
    packet_size: number,
    file: ArrayBuffer,
    deviceId: any
  ): Promise<boolean> {
    //self.start_time = time.time()
    const startTime = new Date().getTime();
    let index = 0;
    let written_size = 0;
    while (written_size < file.byteLength) {
      let sector_size = 0;
      let sequence = 0;
      let crc = 0;
      //let to_read = 0;
      let f_last = false;
      let sector = file.slice(written_size, written_size + 4096);
      if (sector.byteLength === 0) {
        break;
      }
      while (sector_size < sector.byteLength) {
        let to_read = packet_size - 3;
        if (sector_size + to_read > sector.byteLength) {
          to_read = sector.byteLength - sector_size;
          f_last = true;
        }
        let sector_data = sector.slice(sector_size, sector_size + to_read);
        sector_size = sector_size + to_read;
        if (sector_size >= 4096) f_last = true;
        crc = crc16(
          crc,
          new Uint8Array(sector_data, 0, sector_data.byteLength),
          sector_data.byteLength
        );
        if (f_last) sequence = 0xff;
        let packet = Buffer.alloc(to_read + 3);
        packet[0] = index & 0xff;
        packet[1] = (index >> 8) & 0xff;
        packet[2] = sequence;
        Buffer.from(sector_data).copy(packet as any, 3);
        written_size = written_size + sector_data.byteLength;
        //console.log(written_size);

        if (f_last) {
          let p_status = Math.round((100 * written_size) / file.byteLength);
          setProgress(p_status);
          patchItem(deviceId, { progress: p_status });
          setUploadSpeed(written_size * 1000 / 1024 / (new Date().getTime() - startTime));
          let crc_data = Buffer.alloc(2);
          crc_data[0] = crc & 0xff;
          crc_data[1] = (crc >> 8) & 0xff;
          packet = Buffer.concat([packet as any, crc_data]);
        }
        //console.log("Send sector %d sequence %d sector end index %d", index, sequence, packet.length)
        //setfwStatus(0);
        fwStatus = 0;
        expected_index = index;
        //console.log("Send " + index);
        await BleClient.writeWithoutResponse(
          deviceId,
          "00008018-0000-1000-8000-00805f9b34fb",
          "00008020-0000-1000-8000-00805f9b34fb",
          numbersToDataView(Array.prototype.slice.call(packet))
        );
        if (f_last && fwStatus == 0) {
          const fw_ack = await waitForAnsFw(5000);
          if (!fw_ack) {
            console.log("FW NACK");
            return false; // l'appelant gère l'affichage de l'erreur
          }
        }
        sequence = sequence + 1;
      }
      index = index + 1;
    }
    return true;
  }

  //function parseFirmwareNotification(value: DataView): number {
  function parseFirmwareNotification(value: DataView): void {
    //console.log(value);
    if (value.buffer.byteLength >= 20) {
      let crc = value.getUint16(18, true);
      let crc_recv = crc16(0, new Uint8Array(value.buffer, 0, 16), 18);
      if (crc_recv == crc) {
        let fw_ans = value.getUint16(2, true);
        if (fw_ans == 0 && expected_index == value.getUint16(0, true)) {
          fwStatus = 1;
          // ✅ Réveille immédiatement waitForAnsFw (plus d'attente du tick 50 ms)
          if (fwAckResolver) {
            fwAckResolver(true);
            fwAckResolver = null;
          }
          // console.log("OK");
        } else if (fw_ans == 1 && expected_index == value.getUint16(0, true)) {
          fwStatus = 0;
          setStatus("CRC Error");
          console.log("CRC Error");
          //TODO: crc error
        } else if (fw_ans == 2) {
          //&& (expected_index == value.getUint16(0,true))???
          fwStatus = 0;
          setStatus("Index Error");
          console.log("Index Error");
          //TODO: sector index
          // bytes(4 ~ 5) indicates the desired Sector_Index
        } else if (fw_ans == 3 && expected_index == value.getUint16(0, true)) {
          fwStatus = 0;
          setStatus("Payload length Error");
          console.log("Payload length Error");
          //TODO: Payload length error
        }
        //setfwStatus(1);
      }
    }
  }
  function parseCommandNotification(value: DataView): void {
    if (value.buffer.byteLength >= 20) {
      let crc = value.getUint16(18, true);
      let crc_recv = crc16(0, new Uint8Array(value.buffer, 0, 16), 18);
      if (crc_recv == crc) {
        if (value.getUint16(0, true) == 3) {
          if (
            value.getUint16(2, true) == 1 ||
            value.getUint16(2, true) == 2 ||
            value.getUint16(2, true) == 4
          ) {
            let ans = value.getUint16(4, true);
            if (ans == 0) {
              //ok
              //setcmdStatus(1);
              cmdStatus = 1;
              // ✅ Réveille immédiatement waitForAnsCommand
              if (cmdAckResolver) {
                cmdAckResolver(true);
                cmdAckResolver = null;
              }
              console.log(value);
            } else if (ans == 1) {
              if (value.getUint16(2, true) == 1)
                setStatus("NACK on START command");
              else if (value.getUint16(2, true) == 1)
                setStatus("NACK on STOP command");
              //not ok
              cmdStatus = 0;
              //setcmdStatus(0);
            } else if (ans == 3) {
              setStatus("Signature Error");
              //signature error TODO
              //setcmdStatus(0);
              cmdStatus = 0;
            }
          }
        }
      }
    }
  }

  // Abonnements aux notifications OTA (firmware + commandes) d'un appareil
  const startOtaNotifications = async (deviceId: string) => {
    await BleClient.startNotifications(deviceId, OTA_SERVICE, FW_CHAR, (value) =>
      parseFirmwareNotification(value)
    );
    await BleClient.startNotifications(deviceId, OTA_SERVICE, CMD_CHAR, (value) =>
      parseCommandNotification(value)
    );
  };

  const ScanClick = async () => {
    if (runningRef.current) return; // pas de re-scan pendant un enchaînement

    if (isConnected) {
      setConnection(false);
      BleClient.disconnect(clientDevice.deviceId);
    }
    try {
      setShowStatus(false);
      setConnection(false);
      setIsFileLoaded(false);
      setProgressBar(false);
      queueRef.current = []; // nouveau scan = nouvelle file
      syncQueue();

      setDis(false);
      await BleClient.initialize();
      clientDevice = await BleClient.requestDevice({
        services: ["00008018-0000-1000-8000-00805f9b34fb"],
        optionalServices: [numberToUUID(0x180a)],
      });

      await BleClient.connect(clientDevice.deviceId, (deviceId) =>        onDisconnect(deviceId)   );
      setDevice(clientDevice.name as string);
      
      console.log("Connected to device");
      await startOtaNotifications(clientDevice.deviceId);

      try {
        const response = await BleClient.read(
          clientDevice.deviceId,
          numberToUUID(0x180a),
          numberToUUID(0x2a24),
          { timeout: 500 }
        );
        setModel(ab2str(response.buffer));
        setDis(true);
      } catch (error) {
        console.log("timeout 0x2a24");
        setModel("");
      }

      try {
        const response = await BleClient.read(
          clientDevice.deviceId,
          numberToUUID(0x180a),
          numberToUUID(0x2a26),
          { timeout: 500 }
        );
        setSWVersion(ab2str(response.buffer));
        setDis(true);
      } catch (error) {
        console.log("timeout 0x2a26");
        setSWVersion("");
      }
      try {
        const response = await BleClient.read(
          clientDevice.deviceId,
          numberToUUID(0x180a),
          numberToUUID(0x2a27),
          { timeout: 500 }
        );
        setHWVersion(ab2str(response.buffer));
        setDis(true);
      } catch (error) {
        console.log("timeout 0x2a27");
        setHWVersion("");
      }
      try {
        const response = await BleClient.read(
          clientDevice.deviceId,
          numberToUUID(0x180a),
          numberToUUID(0x2a29),
          { timeout: 500 }
        );
        setManufacturer(ab2str(response.buffer));
        setDis(true);
      } catch (error) {
        console.log("timeout 0x2a29");
        setManufacturer("");
      }
      //presentToast('middle', "Connected");
      setConnection(true);
    } catch (e) {
      console.error(e);
    }
  };
  const DisconnectClick = () => {
    if (runningRef.current) return; // pas pendant un enchaînement
    setIsFileLoaded(false);
    setProgressBar(false);
    queueRef.current = [];
    syncQueue();

    if (isConnected) {
      setConnection(false);
      BleClient.disconnect(clientDevice.deviceId);
    }
  };

  // ✅ Ajoute un appareil à la file — utilisable à tout moment, même pendant
  // un flash. Dans le BzookBooster, la multi-sélection déclenche ce bouton
  // automatiquement pour chaque appareil coché.
  const AddDeviceClick = async () => {
    try {
      const extra = await BleClient.requestDevice({
        services: [OTA_SERVICE],
        optionalServices: [numberToUUID(0x180a)],
      });
      const name = (extra.name as string) || extra.deviceId;
      // Verrouillage du type : cibles avec cibles, carabines avec carabines
      if (deviceTypeOf(name) !== deviceTypeOf(device)) {
        setStatus(`${name} ignoré : pas du même type que ${device}`);
        setBarStatus("warning");
        setShowStatus(true);
        return;
      }
      if (
        extra.deviceId === clientDevice?.deviceId ||
        queueRef.current.some((q) => q.deviceId === extra.deviceId)
      ) {
        return; // déjà dans la file
      }
      const item: QueueItem = {
        deviceId: extra.deviceId,
        name,
        status: "pending",
        detail: "",
        progress: 0,
        curVersion: "",
      };
      queueRef.current.push(item);
      syncQueue();
      // Lit la version tout de suite pour l'afficher — mais pas pendant un flash
      // en cours (on évite une 2e connexion GATT qui perturberait le transfert).
      if (!runningRef.current) {
        try {
          await BleClient.connect(extra.deviceId, (id) =>
            console.log(`device ${id} disconnected`)
          );
          item.curVersion = await readSwVersion(extra.deviceId);
          await BleClient.disconnect(extra.deviceId);
          syncQueue();
        } catch (e) {
          console.log("Lecture version différée", e);
        }
      }
    } catch (e) {
      console.log("Ajout annulé", e);
    }
  };
  // Function that returns a Promise and resolves when the condition is settled
  function waitForAnsCommand(timeout: number): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      // ACK déjà arrivé entre l'envoi et l'attente : on résout tout de suite.
      if (cmdStatus === ACK) {
        resolve(true);
        return;
      }
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cmdAckResolver = null;
        console.log("timeout1");
        resolve(false);
      }, timeout);
      // Résolution pilotée par la notification (plus de polling 50 ms).
      cmdAckResolver = (v: boolean) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(v);
      };
    });
  }

  function waitForAnsFw(timeout: number): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      // ACK déjà arrivé entre le write et l'attente : on résout tout de suite.
      if (fwStatus == 1) {
        resolve(true);
        return;
      }
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        fwAckResolver = null;
        console.log("timeout2");
        resolve(false);
      }, timeout);
      // Résolution pilotée par la notification (plus de polling 50 ms).
      fwAckResolver = (v: boolean) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(v);
      };
    });
  }
  useEffect(() => {
    if (!device) return;
    // Le firmware retenu pour l'appareil précédent ne vaut plus rien ici : une
    // carabine et une cible ne lisent pas le même dossier.
    setSelectedFile(null);
    setFiles([]);

    fetch(getUrlFilesFromDeviceName())
      .then(response => response.text())
      .then(data => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data, 'text/html');
        const links = Array.from(doc.querySelectorAll('a'))
          .map(link => link.getAttribute('href'))
          .filter((href): href is string => !!href && href.endsWith('.bin'));
        const sorted = sortByVersionDesc(links);
        setFiles(sorted);
        // ✅ V1.9 — présélection de la dernière version compatible. Le dossier
        // interrogé est déjà celui du type d'appareil connecté (BZBOX ou
        // KARABINE) : la version la plus haute qu'il contient est donc la bonne.
        // L'opérateur reste libre d'en choisir une autre dans la liste.
        const latest = sorted.find((f) => extractVersion(f));
        if (latest) setSelectedFile(latest);
      })
      .catch((e) => console.log("Liste des firmwares indisponible", e));
  }, [device]);

  const getUrlFilesFromDeviceName = ()=>{
    let url = "";
    if(device?.includes('BZBOX FLASH')){ url = 'https://ota.bzook.fr/binaires/BZBOX/'};
    if(device?.includes('KARABINE FLASH')){ url = 'https://ota.bzook.fr/binaires/KARABINE/'}
    return url;
  };
  // Séquence OTA complète sur un appareil déjà connecté (notifications actives).
  // Retourne null si succès, sinon le message d'erreur.
  const doOta = async (
    deviceId: string,
    fileBuf: ArrayBuffer
  ): Promise<string | null> => {
    let buffer = Buffer.alloc(20);
    console.log("Start App OTA");
    buffer[0] = 0x01;
    buffer[1] = 0x00;
    buffer.writeUInt32LE(fileBuf.byteLength, 2);
    let crc = crc16(0, buffer, 18);
    buffer.writeUInt16LE(crc, 18);
    cmdStatus = 0;
    await BleClient.write(
      deviceId,
      OTA_SERVICE,
      CMD_CHAR,
      numbersToDataView(Array.prototype.slice.call(buffer))
    );
    if (!(await waitForAnsCommand(5000))) return "pas de réponse au démarrage OTA";
    console.log("cmd acked");
    setProgress(0);
    setProgressBar(true);
    if (!(await send_firmware(510, fileBuf, deviceId))) return "échec du transfert";
    console.log("Stop OTA");
    buffer.fill(0);
    buffer[0] = 0x02;
    buffer[1] = 0x00;
    crc = crc16(0, buffer, 18);
    buffer.writeUInt16LE(crc, 18);
    cmdStatus = 0;
    await BleClient.write(
      deviceId,
      OTA_SERVICE,
      CMD_CHAR,
      numbersToDataView(Array.prototype.slice.call(buffer))
    );
    if (!(await waitForAnsCommand(5000))) return "pas de confirmation de fin";
    return null;
  };

  // Traite la file jusqu'à épuisement — les appareils ajoutés pendant un flash
  // sont pris en compte au tour suivant.
  const runQueue = async (
    fileBuf: ArrayBuffer,
    force: boolean,
    target: string | null
  ) => {
    let item: QueueItem | undefined;
    while ((item = queueRef.current.find((q) => q.status === "pending"))) {
      const it = item;
      patchItem(it.deviceId, { status: "updating" });
      const t0 = new Date().getTime();
      startChrono();
      let err: string | null = null;
      let skipped = false;
      try {
        await BleClient.connect(it.deviceId, (id) =>
          console.log(`device ${id} disconnected`)
        );
        await startOtaNotifications(it.deviceId);
        // Relit la version au moment du flash (source de vérité pour le skip)
        const cur = await readSwVersion(it.deviceId);
        it.curVersion = cur || it.curVersion;
        if (skipDecision(it.curVersion, force, target)) {
          skipped = true;
        } else {
          err = await doOta(it.deviceId, fileBuf);
        }
      } catch (e: any) {
        err = e?.message || "erreur de connexion BLE";
      }
      stopChrono();
      patchItem(
        it.deviceId,
        skipped
          ? { status: "skipped", detail: `déjà en ${it.curVersion || target}` }
          : err
          ? { status: "failed", detail: err }
          : {
              status: "done",
              detail: formatDuration((new Date().getTime() - t0) / 1000),
            }
      );
      try {
        await BleClient.disconnect(it.deviceId);
      } catch (e) {}
    }
  };

  const OtaClick = async () => {
    if (!selectedFile || runningRef.current) return;
    // Fige les paramètres pour toute la session d'enchaînement
    const force = forceUpdate;
    const target = extractVersion(selectedFile);
    runningRef.current = true;
    setRunning(true);
    setShowStatus(false);
    setIsFileLoaded(false);

    const response = await fetch(`${getUrlFilesFromDeviceName()}${selectedFile}`);
    const fileBuf = await response.arrayBuffer();
    setOtaFile(fileBuf);

    // L'appareil connecté est le premier de la file (version déjà lue = SWVersion)
    queueRef.current.unshift({
      deviceId: clientDevice.deviceId,
      name: device,
      status: "updating",
      detail: "",
      progress: 0,
      curVersion: SWVersion,
    });
    syncQueue();

    const t0 = new Date().getTime();
    let err: string | null = null;
    let skipped = false;
    if (skipDecision(SWVersion, force, target)) {
      skipped = true;
    } else {
      startChrono();
      try {
        err = await doOta(clientDevice.deviceId, fileBuf);
      } catch (e: any) {
        err = e?.message || "erreur BLE";
      }
      stopChrono();
    }
    patchItem(
      clientDevice.deviceId,
      skipped
        ? { status: "skipped", detail: `déjà en ${SWVersion || target}` }
        : err
        ? { status: "failed", detail: err }
        : {
            status: "done",
            detail: formatDuration((new Date().getTime() - t0) / 1000),
          }
    );
    try {
      await BleClient.disconnect(clientDevice.deviceId);
    } catch (e) {}
    setConnection(false);

    // Puis tous les appareils ajoutés à la file (même pendant les flashs)
    await runQueue(fileBuf, force, target);

    runningRef.current = false;
    setRunning(false);
    setProgressBar(false);
    const ok = queueRef.current.filter((q) => q.status === "done").length;
    const ko = queueRef.current.filter((q) => q.status === "failed").length;
    const sk = queueRef.current.filter((q) => q.status === "skipped").length;
    setStatus(
      `Terminé : ${ok} mise(s) à jour${sk ? `, ${sk} déjà à jour` : ""}${
        ko ? `, ${ko} échec(s)` : ""
      }`
    );
    setBarStatus(ko ? "warning" : "success");
    setShowStatus(true);
  };

 
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            {!isConnected && !running && !modeMaster && !modeUsine && (
              <IonButton
                className="custom-button-scan-ota"
                onClick={() => {
                  ScanClick();
                }}
              >
                Scan
              </IonButton>
            )}
            {/* Le Master n'a pas de BLE : il se met à jour par USB, via un flux
                séparé qui ne passe pas par le scan */}
            {!isConnected && !running && !modeMaster && !modeParc && !modeUsine && (
              <IonButton color="tertiary" onClick={() => setModeMaster(true)}>
                Master USB
              </IonButton>
            )}
            {!isConnected && !running && !modeMaster && !modeParc && !modeUsine && (
              <IonButton color="secondary" onClick={() => setModeParc(true)}>
                Parc
              </IonButton>
            )}

            {isConnected && (
              <IonButton
                color="danger"
                onClick={() => {
                  DisconnectClick();
                }}
              >
                Disconnect
              </IonButton>
            )}
          </IonButtons>
          {/* Tenu à l'écart des trois autres, à l'opposé de la barre : cet
              écran reprogramme une carte à zéro, il ne doit pas se cliquer
              dans la foulée d'un « Scan ». */}
          <IonButtons slot="end">
            {!isConnected && !running && !modeMaster && !modeParc && !modeUsine && (
              <IonButton
                fill="outline"
                className="ota-btn-technicien"
                onClick={() => setModeUsine(true)}
              >
                ⚠️ Techniciens
              </IonButton>
            )}
          </IonButtons>
          <IonTitle>BZooK OTA</IonTitle>
          {/* ✅ Version de l'app toujours visible pour vérifier qu'on a bien la dernière */}
          <IonText slot="end" className="app-version">
            App {version}
          </IonText>
        </IonToolbar>
      </IonHeader>
      {showStatus && (<IonCard color={barStatus}>
        <IonCardContent>
          <IonGrid>
            <IonRow className="ion-justify-content-center ion-align-items-center">
              <IonText>{status}</IonText>
            </IonRow>
          </IonGrid>
        </IonCardContent></IonCard>
      )}
      {modeMaster && <FlashMaster onFermer={() => setModeMaster(false)} />}
      {modeParc && <ParcCibles onFermer={() => setModeParc(false)} />}
      {modeUsine && <FlashUsine onFermer={() => setModeUsine(false)} />}

      {!modeMaster && !modeParc && !modeUsine && (isConnected || queue.length > 0) && (
        <IonContent class="ion-padding">
          <IonHeader collapse="condense">
            <IonToolbar>
              <IonTitle size="large">Mise à jour</IonTitle>
            </IonToolbar>
          </IonHeader>
          <h1>
            {running
              ? queue.find((q) => q.status === "updating")?.name ?? device
              : device}
          </h1>
          {DISInfo && isConnected && (
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Device Information</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonGrid>
                  <IonRow>
                    <IonCol>
                      <IonCardSubtitle>
                        Manufacturer: {Manufacturer}
                      </IonCardSubtitle>
                      <IonCardSubtitle>Model: {Model}</IonCardSubtitle>
                      <IonCardSubtitle>HW Version: {HWVersion}</IonCardSubtitle>
                      <IonCardSubtitle>FW Version: {SWVersion}</IonCardSubtitle>
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonCardContent></IonCard>
          )}
          <IonCard>
          <IonCardContent>
            <IonSelect value={selectedFile} placeholder="Select One" disabled={running} onIonChange={e => setSelectedFile(e.detail.value)}>
              {files.map(file => (
                <IonSelectOption key={file} value={file}>{file}</IonSelectOption>
              ))}
            </IonSelect>
            {targetVersion && (
              <IonText>
                <p className="ota-target-version">
                  🎯 Version cible : {targetVersion}
                  {/* La liste est triée du plus récent au plus ancien : si le
                      fichier choisi est le premier, c'est la dernière publiée. */}
                  {selectedFile === files[0] && " (dernière disponible)"}
                </p>
              </IonText>
            )}
            {/* ✅ Par défaut, un appareil déjà dans la version cible est sauté.
                Cocher pour flasher systématiquement quelle que soit la version. */}
            <IonItem lines="none">
              <IonCheckbox
                slot="start"
                checked={forceUpdate}
                disabled={running}
                onIonChange={(e) => setForceUpdate(e.detail.checked)}
              />
              <IonLabel>Forcer la mise à jour (ignorer la version)</IonLabel>
            </IonItem>
            <IonButton onClick={OtaClick} disabled={!selectedFile || running}>
              Start OTA
            </IonButton>
            {/* id="addQueueBtn" : cliqué automatiquement par le BzookBooster
                pour chaque appareil coché lors d'une multi-sélection */}
            <IonButton
              id="addQueueBtn"
              color="medium"
              onClick={AddDeviceClick}
              disabled={!deviceTypeOf(device)}
            >
              ➕ Ajouter à la file
            </IonButton>
            {selectedFile && (
                        <ProgressBar
                          completed={progress}
                          labelAlignment="center"
                          bgColor="#3880ff"
                        />
                      )}
            {showBar && (
              <IonText>
                <p className="ota-stats">
                  ⏱️ Durée : {formatDuration(elapsed)} • {upload_speed.toFixed(1)}{" "}
                  Ko/s
                </p>
              </IonText>
            )}
          </IonCardContent>
           </IonCard>

          {queue.length > 0 && (
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>
                  File d'attente (
                  {queue.filter((q) => q.status === "done").length}/
                  {queue.length})
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonGrid>
                  {queue.map((q) => (
                    <IonRow key={q.deviceId} className="ion-align-items-center">
                      <IonCol size="5">
                        <IonLabel>{q.name}</IonLabel>
                      </IonCol>
                      <IonCol size="3">
                        <IonLabel className="ota-queue-version">
                          {extractVersion(q.curVersion)
                            ? `v${extractVersion(q.curVersion)}`
                            : q.curVersion || "v ?"}
                        </IonLabel>
                      </IonCol>
                      <IonCol size="4">
                        <IonLabel>
                          {q.status === "pending" && "⏳ En attente"}
                          {q.status === "updating" && `🔄 ${q.progress} %`}
                          {q.status === "done" && `✅ OK (${q.detail})`}
                          {q.status === "skipped" && `⏭️ à jour`}
                          {q.status === "failed" && `❌ ${q.detail}`}
                        </IonLabel>
                      </IonCol>
                    </IonRow>
                  ))}
                </IonGrid>
              </IonCardContent>
            </IonCard>
          )}

        </IonContent>
      )}

      <IonFooter>
        <IonToolbar >
          <IonTitle>App {version}</IonTitle>

        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default Ota;
